import { apiClient } from '@isocel/front-common';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import { InventoryStatus, PackageInventoryItem, StoreInventory } from './types';

export const createStoreInventory = async (payload: {
    organizationId: string;
    storeId: string;
    packagesInventory: PackageInventoryItem[];
}): Promise<StoreInventory> => {
    const { data } = await apiClient().post('/storeInventory', payload);
    return data;
};

export const getStoreInventories = async (params: {
    organizationId: string;
    storeId?: string;
}): Promise<StoreInventory[]> => {
    const { data } = await apiClient().get('/storeInventory', { params });
    return data;
};

export const updateInventoryItemStatus = async (payload: {
    inventoryId: string;
    packageId: string;
    status: InventoryStatus;
}): Promise<StoreInventory> => {
    const { inventoryId, ...body } = payload;
    const { data } = await apiClient().put(`/storeInventory/${inventoryId}/status`, body);
    return data;
};

export interface InventoryStoreConfig {
    storeId: string;
    enabled: boolean;
}

export interface InventoryConfig {
    organizationId: string;
    enabled: boolean;
    stores?: InventoryStoreConfig[];
}

export const getInventoryConfig = async (organizationId: string): Promise<InventoryConfig> => {
    const { data } = await apiClient().get('/storeInventory/config', { params: { organizationId } });
    return data;
};

export const updateInventoryConfig = async (payload: {
    organizationId: string;
    enabled: boolean;
}): Promise<InventoryConfig> => {
    const { data } = await apiClient().patch('/storeInventory/config', payload);
    return data;
};

export const updateInventoryStoreConfig = async (payload: {
    organizationId: string;
    storeId: string;
    enabled: boolean;
}): Promise<InventoryConfig> => {
    const { data } = await apiClient().patch('/storeInventory/config/store', payload);
    return data;
};

export const useInventoryConfig = (organizationId?: string) => {
    return useQuery<InventoryConfig, Error>(
        ['inventoryConfig', organizationId],
        async () => await getInventoryConfig(organizationId as string),
        { enabled: !!organizationId }
    );
};

export const useUpdateInventoryConfig = () => {
    const queryClient = useQueryClient();
    return useMutation(updateInventoryConfig, {
        onSuccess: (data) => {
            queryClient.setQueryData(['inventoryConfig', data.organizationId], data);
        },
    });
};

export const useUpdateInventoryStoreConfig = () => {
    const queryClient = useQueryClient();
    return useMutation(updateInventoryStoreConfig, {
        onSuccess: (data) => {
            queryClient.setQueryData(['inventoryConfig', data.organizationId], data);
        },
    });
};

export const isStoreInventoryEnabled = (config: InventoryConfig | undefined, storeId?: string): boolean => {
    // No specific store => reflect the master flag
    if (!storeId) return config?.enabled !== false;
    // A store is enabled by default; disabled only if explicitly set to false in the per-store list
    const storeEntry = config?.stores?.find((s) => s.storeId === storeId);
    return storeEntry?.enabled !== false;
};
